import useToggleDriveMode from "@/components/AppHeader/hooks/useToggleDriveMode";
import { CursorType } from "@/store/globalSettingsSlice";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { useChangeLedgerStore } from "@/store/zustand/changeLedger";
import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

export const useOnKeyDown = () => {
  const { toggleCursor } = useToggleDriveMode({
    fromKey: true,
  });

  const setIsSpacePressed = useCanvasStore((state) => state.setIsSpacePressed);
  const setIsMetaPressed = useCanvasStore((state) => state.setIsMetaPressed);
  const setIsCtrlPressed = useCanvasStore((state) => state.setIsCtrlPressed);

  const addRedoChange = useChangeLedgerStore((state) => state.addRedoChange);
  const addUndoChange = useChangeLedgerStore((state) => state.addUndoChange);

  const setTriggerDeleteElement = useCanvasStore(
    (state) => state.setTriggerDeleteElement
  );

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType
  );
  const cursorTypeFromKeyDown = useSelector(
    (state: any) => state.globalSettings.cursorTypeFromKeyDown
  );

  const cursorTypeRef = useRef(cursorType);
  const cursorTypeFromKeyDownRef = useRef(cursorTypeFromKeyDown);
  const toggleCursorRef = useRef(toggleCursor);

  useEffect(() => {
    cursorTypeRef.current = cursorType;
  }, [cursorType]);

  useEffect(() => {
    cursorTypeFromKeyDownRef.current = cursorTypeFromKeyDown;
  }, [cursorTypeFromKeyDown]);

  useEffect(() => {
    toggleCursorRef.current = toggleCursor;
  }, [toggleCursor]);

  const onKeyDown = useCallback((e) => {
    if (e.repeat) return;

    const activeElement = e.activeElement || document.activeElement;
    const isEditableElement =
      activeElement.tagName === "INPUT" ||
      activeElement.tagName === "TEXTAREA" ||
      activeElement.isContentEditable;

    if (isEditableElement) {
      return;
    }

    if (e.code === "Space") {
      setIsSpacePressed(true);
    }

    if (e.key === "Meta") {
      setIsMetaPressed(true);
    }

    if (e.key === "Control") {
      setIsCtrlPressed(true);
    }

    if (e.key === "d" && cursorTypeRef.current !== CursorType.Drive) {
      toggleCursorRef.current();
    }

    if (e.key === "z" && (e.metaKey || e.ctrlKey)) {
      if (e.shiftKey) {
        addRedoChange();
      } else {
        addUndoChange();
      }

      e?.preventDefault?.();
      e?.stopPropagation?.();
    }

    if (e.key === "backspace" || e.key === "Delete") {
      setTriggerDeleteElement(true);
    }
  }, []);

  return onKeyDown;
};

export const useOnKeyUp = () => {
  const { toggleCursor } = useToggleDriveMode({
    fromKey: true,
  });

  const setIsSpacePressed = useCanvasStore((state) => state.setIsSpacePressed);
  const setIsMetaPressed = useCanvasStore((state) => state.setIsMetaPressed);
  const setIsCtrlPressed = useCanvasStore((state) => state.setIsCtrlPressed);

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType
  );
  const cursorTypeFromKeyDown = useSelector(
    (state: any) => state.globalSettings.cursorTypeFromKeyDown
  );

  const cursorTypeRef = useRef(cursorType);
  const cursorTypeFromKeyDownRef = useRef(cursorTypeFromKeyDown);
  const toggleCursorRef = useRef(toggleCursor);

  useEffect(() => {
    cursorTypeRef.current = cursorType;
  }, [cursorType]);

  useEffect(() => {
    cursorTypeFromKeyDownRef.current = cursorTypeFromKeyDown;
  }, [cursorTypeFromKeyDown]);

  useEffect(() => {
    toggleCursorRef.current = toggleCursor;
  }, [toggleCursor]);

  const onKeyUp = useCallback((e) => {
    if (e.repeat) return;

    const activeElement = e.activeElement || document.activeElement;
    const isEditableElement =
      activeElement.tagName === "INPUT" ||
      activeElement.tagName === "TEXTAREA" ||
      activeElement.isContentEditable;

    if (isEditableElement) {
      return;
    }

    if (e.code === "Space") {
      setIsSpacePressed(false);
    }

    if (e.key === "Meta") {
      setIsMetaPressed(false);
    }

    if (e.key === "Control") {
      setIsCtrlPressed(false);
    }

    if (e.key === "Escape" && cursorTypeRef.current === "drive") {
      toggleCursorRef.current();
    }

    if (
      e.key === "d" &&
      cursorTypeRef.current === "drive" &&
      cursorTypeFromKeyDownRef.current
    ) {
      toggleCursorRef.current();
    }
  }, []);

  return onKeyUp;
};

export const useOnMouseDown = () => {
  const setIsMiddleMousePressed = useCanvasStore((state) => {
    return state.setIsMiddleMousePressed;
  });

  return useCallback((e: MouseEvent) => {
    if (e.button === 1) {
      setIsMiddleMousePressed(true);
    }
  }, []);
};

export const useOnMouseUp = () => {
  const setIsMiddleMousePressed = useCanvasStore(
    (state) => state.setIsMiddleMousePressed
  );

  return useCallback((e: MouseEvent) => {
    if (e.button === 1) {
      setIsMiddleMousePressed(false);
    }
  }, []);
};
